<template>
  <div class="client-config-management">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h2>{{ $t('clientConfig.title') }}</h2>
      </div>
    </div>

    <!-- 统计卡片 -->
    <a-row :gutter="16" class="stats-row">
      <a-col :span="6">
        <a-card>
          <a-statistic :title="$t('clientConfig.availableApis')" :value="clientStats.available_apis" />
        </a-card>
      </a-col>
      <a-col :span="6">
        <a-card>
          <a-statistic :title="$t('clientConfig.todayAccess')" :value="clientStats.today_access" />
        </a-card>
      </a-col>
      <a-col :span="6">
        <a-card>
          <a-statistic :title="$t('clientConfig.activeClients')" :value="clientStats.active_clients" />
        </a-card>
      </a-col>
      <a-col :span="6">
        <a-card>
          <a-statistic :title="$t('clientConfig.encryptedTransmission')" :value="100" suffix="%" />
        </a-card>
      </a-col>
    </a-row>

    <!-- 搜索和筛选 -->
    <a-card class="search-card">
      <a-form layout="inline" :model="filters">

        <a-form-item :label="$t('clientConfig.apiType')">
          <a-select
            v-model:value="filters.api_type"
            :placeholder="$t('clientConfig.apiType')"
            style="width: 120px"
            allow-clear
            @change="handleFilterChange"
          >
            <a-select-option
              v-for="option in API_TYPE_OPTIONS"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('clientConfig.clientStatus')">
          <a-select
            v-model:value="filters.client_enabled"
            :placeholder="$t('clientConfig.clientStatus')"
            style="width: 120px"
            allow-clear
            @change="handleFilterChange"
          >
            <a-select-option :value="true">{{ $t('clientConfig.enabled') }}</a-select-option>
            <a-select-option :value="false">{{ $t('clientConfig.disabled') }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('common.search')">
          <a-input-search
            v-model:value="filters.search"
            :placeholder="$t('clientConfig.searchApiName')"
            style="width: 200px"
            @search="handleFilterChange"
          />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button @click="refreshData">
              <template #icon><ReloadOutlined /></template>
              {{ $t('clientConfig.refresh') }}
            </a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 客户端配置列表 -->
    <a-card>
      <a-table
        :columns="clientColumns"
        :data-source="filteredApiConfigs"
        :loading="loading"
        :pagination="pagination"
        @change="handleTableChange"
        row-key="id"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'api_type'">
            <a-tag :color="getAPITypeColor(record.api_type)">
              {{ getAPITypeLabel(record.api_type) }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'client_config_enabled'">
            <a-switch
              :checked="record.client_config_enabled"
              @change="(checked: boolean) => handleClientAccessToggle(record, checked)"
              :loading="record.clientToggleLoading"
            />
          </template>
          <template v-else-if="column.key === 'client_access_count'">
            <a-tag color="blue">{{ record.client_access_count || 0 }}</a-tag>
          </template>
          <template v-else-if="column.key === 'last_client_access_at'">
            <span v-if="record.last_client_access_at">
              {{ formatDateTime(record.last_client_access_at) }}
            </span>
            <span v-else class="text-gray-400">{{ $t('clientConfig.neverAccessed') }}</span>
          </template>
          <template v-else-if="column.key === 'action'">
            <a-space>
              <a-button 
                type="link" 
                size="small" 
                @click="showClientConfigModal(record)"
              >
                {{ $t('clientConfig.config') }}
              </a-button>
              <a-button 
                type="link" 
                size="small" 
                @click="showAccessLogsModal(record)"
              >
                {{ $t('clientConfig.accessLogs') }}
              </a-button>
              <a-button 
                type="link" 
                size="small" 
                @click="testClientConfig(record)"
                :loading="record.testLoading"
              >
                {{ $t('clientConfig.test') }}
              </a-button>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 客户端配置编辑模态框 -->
    <a-modal
      v-model:open="clientConfigModalVisible"
      :title="$t('clientConfig.clientConfigTitle')"
      :width="600"
      @ok="handleClientConfigSave"
      @cancel="handleClientConfigCancel"
    >
      <a-form
        ref="clientConfigFormRef"
        :model="clientConfigForm"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
      >
        <a-form-item :label="$t('clientConfig.apiName')">
          <a-input v-model:value="clientConfigForm.name" disabled />
        </a-form-item>
        <a-form-item :label="$t('clientConfig.type')">
          <a-tag :color="getAPITypeColor(clientConfigForm.api_type)">
            {{ getAPITypeLabel(clientConfigForm.api_type) }}
          </a-tag>
        </a-form-item>
        <a-form-item :label="$t('clientConfig.clientAccess')">
          <a-switch v-model:checked="clientConfigForm.client_config_enabled" />
        </a-form-item>
        <a-form-item :label="$t('clientConfig.apiKey')">
          <a-input-password 
            v-model:value="clientConfigForm.api_key"
            :placeholder="$t('clientConfig.enterNewApiKey')"
            :disabled="!clientConfigForm.client_config_enabled"
          />
          <div class="form-help-text">
            {{ $t('clientConfig.currentKey') }}{{ clientConfigForm.current_api_key_masked || $t('clientConfig.notSet') }}
          </div>
        </a-form-item>
        <a-form-item :label="$t('clientConfig.clientEndpoint')">
          <a-input 
            v-model:value="clientConfigForm.client_endpoint" 
            :placeholder="$t('clientConfig.useDefaultEndpoint')"
            :disabled="!clientConfigForm.client_config_enabled"
          />
        </a-form-item>
        <a-form-item :label="$t('clientConfig.clientRegion')">
          <a-input 
            v-model:value="clientConfigForm.client_region" 
            :placeholder="$t('clientConfig.useDefaultRegion')"
            :disabled="!clientConfigForm.client_config_enabled"
          />
        </a-form-item>
        <a-form-item :label="$t('clientConfig.extraConfig')">
          <a-textarea 
            v-model:value="clientConfigForm.client_extra_config_json"
            :placeholder="$t('clientConfig.jsonFormatExtraConfig')"
            :rows="4"
          />
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 访问日志模态框 -->
    <a-modal
      v-model:open="accessLogsModalVisible"
      :title="$t('clientConfig.clientAccessLogs')"
      :width="1000"
      :footer="null"
    >
      <a-table
        :columns="accessLogColumns"
        :data-source="accessLogs"
        :loading="accessLogsLoading"
        :pagination="accessLogsPagination"
        @change="handleAccessLogsTableChange"
        row-key="id"
        size="small"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'response_status'">
            <a-tag :color="record.response_status === 200 ? 'green' : 'red'">
              {{ record.response_status }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'response_time'">
            <span>{{ record.response_time }}ms</span>
          </template>
          <template v-else-if="column.key === 'accessed_at'">
            {{ formatDateTime(record.accessed_at) }}
          </template>
        </template>
      </a-table>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import {
  ReloadOutlined
} from '@ant-design/icons-vue'
import { apiConfigApi } from '@/modules/system/api/api-config'
import { clientConfigAdminApi } from '@/modules/system/api/client-config'
import { API_TYPE_OPTIONS } from '@/api/api-constants'
import type { APIConfig } from '@/modules/system/api/api-config'
import type { ClientConfigAccessLog } from '@/modules/system/api/client-config'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// 响应式数据
const loading = ref(false)
const activeTab = ref('client')
const clientConfigModalVisible = ref(false)
const accessLogsModalVisible = ref(false)
const accessLogsLoading = ref(false)

const apiConfigs = ref<APIConfig[]>([])
const accessLogs = ref<ClientConfigAccessLog[]>([])

// 统计数据
const clientStats = reactive({
  available_apis: 0,
  today_access: 0,
  active_clients: 0
})

// 分页
const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total: number) => t('common.totalRecords', { total }),
  // 使用 onChange 处理页码变化
  onChange: (page: number, pageSize: number) => {
    pagination.current = page
    pagination.pageSize = pageSize
    loadApiConfigs()
  },
  // 使用 onShowSizeChange 处理每页数量变化
  onShowSizeChange: (current: number, size: number) => {
    pagination.current = current
    pagination.pageSize = size
    loadApiConfigs()
  }
})

const accessLogsPagination = reactive({
  current: 1,
  pageSize: 10,
  total: 0,
  showSizeChanger: true,
  showTotal: (total: number) => t('common.totalRecords', { total }),
  // 使用 onChange 处理页码变化
  onChange: (page: number, pageSize: number) => {
    accessLogsPagination.current = page
    accessLogsPagination.pageSize = pageSize
    loadAccessLogs()
  },
  // 使用 onShowSizeChange 处理每页数量变化
  onShowSizeChange: (current: number, size: number) => {
    accessLogsPagination.current = current
    accessLogsPagination.pageSize = size
    loadAccessLogs()
  }
})

// 过滤器
const filters = reactive({
  api_type: undefined,
  client_enabled: undefined,
  search: ''
})

// 客户端配置表单
const clientConfigForm = reactive({
  id: 0,
  name: '',
  api_type: '',
  client_config_enabled: false,
  api_key: '',
  current_api_key_masked: '',
  client_endpoint: '',
  client_region: '',
  client_extra_config_json: ''
})

const clientConfigFormRef = ref()

// 计算属性
const filteredApiConfigs = computed(() => {
  let filtered = apiConfigs.value

  if (filters.api_type) {
    filtered = filtered.filter(item => item.api_type === filters.api_type)
  }

  if (filters.client_enabled !== undefined) {
    filtered = filtered.filter(item => item.client_config_enabled === filters.client_enabled)
  }

  if (filters.search) {
    const search = filters.search.toLowerCase()
    filtered = filtered.filter(item => 
      item.name.toLowerCase().includes(search) ||
      (item.description && item.description.toLowerCase().includes(search))
    )
  }

  return filtered
})

// 表格列定义
const clientColumns = computed(() => [
  {
    title: t('clientConfig.apiName'),
    dataIndex: 'name',
    key: 'name',
    width: 200
  },
  {
    title: t('clientConfig.type'),
    dataIndex: 'api_type',
    key: 'api_type',
    width: 100
  },
  {
    title: t('clientConfig.clientAccess'),
    dataIndex: 'client_config_enabled',
    key: 'client_config_enabled',
    width: 120
  },
  {
    title: t('clientConfig.accessCount'),
    dataIndex: 'client_access_count',
    key: 'client_access_count',
    width: 100
  },
  {
    title: t('clientConfig.lastAccess'),
    dataIndex: 'last_client_access_at',
    key: 'last_client_access_at',
    width: 180
  },
  {
    title: t('clientConfig.operation'),
    key: 'action',
    width: 200
  }
])

const accessLogColumns = computed(() => [
  {
    title: t('clientConfig.userId'),
    dataIndex: 'user_id',
    key: 'user_id',
    width: 80
  },
  {
    title: t('clientConfig.clientType'),
    dataIndex: 'client_type',
    key: 'client_type',
    width: 100
  },
  {
    title: t('clientConfig.ipAddress'),
    dataIndex: 'ip_address',
    key: 'ip_address',
    width: 120
  },
  {
    title: t('clientConfig.responseStatus'),
    dataIndex: 'response_status',
    key: 'response_status',
    width: 100
  },
  {
    title: t('clientConfig.responseTime'),
    dataIndex: 'response_time',
    key: 'response_time',
    width: 100
  },
  {
    title: t('clientConfig.accessTime'),
    dataIndex: 'accessed_at',
    key: 'accessed_at',
    width: 180
  }
])

// 方法
const loadApiConfigs = async () => {
  try {
    loading.value = true
    const response = await apiConfigApi.getApiConfigs({
      page: pagination.current,
      size: pagination.pageSize,
      api_type: filters.api_type,
      status: undefined,
      search: filters.search
    })

    if (!response?.items || !Array.isArray(response.items)) {
      console.error('API 配置列表响应格式异常:', response)
      message.error(t('clientConfig.loadApiConfigFailed'))
      apiConfigs.value = []
      pagination.total = 0
      return
    }

    apiConfigs.value = response.items.map(item => ({
      ...item,
      clientToggleLoading: false,
      testLoading: false
    }))
    
    pagination.total = response.total
    
    // 更新统计数据
    updateClientStats()
  } catch (error) {
    console.error('加载API配置失败:', error)
  } finally {
    loading.value = false
  }
}

const updateClientStats = () => {
  const available = apiConfigs.value.filter(item => item.client_config_enabled).length
  const totalAccess = apiConfigs.value.reduce((sum, item) => sum + (item.client_access_count || 0), 0)
  
  clientStats.available_apis = available
  clientStats.today_access = totalAccess // 简化统计
  clientStats.active_clients = available
}

const refreshData = () => {
  loadApiConfigs()
}

const handleFilterChange = () => {
  pagination.current = 1
  loadApiConfigs()
}

// 处理表格变化（排序和筛选）
// 注意：分页变化已经通过 pagination 配置的 onChange/onShowSizeChange 处理
// @change 事件会传递三个参数：(pagination, filters, sorter)
const handleTableChange = (pag: any, filters: any, sorter: any) => {
  // 分页变化已经在 pagination 的 onChange/onShowSizeChange 中处理
  // 这里只处理排序和筛选（如果需要的话）
}

const handleClientAccessToggle = async (record: APIConfig, checked: boolean) => {
  try {
    record.clientToggleLoading = true
    
    const response = await clientConfigAdminApi.toggleClientAccess(record.id)
    
    record.client_config_enabled = response.client_config_enabled
    message.success(response.message)
    updateClientStats()
  } catch (error) {
    console.error('切换客户端访问状态失败:', error)
  } finally {
    record.clientToggleLoading = false
  }
}

const showClientConfigModal = (record: APIConfig) => {
  Object.assign(clientConfigForm, {
    id: record.id,
    name: record.name,
    api_type: record.api_type,
    client_config_enabled: record.client_config_enabled,
    api_key: '',
    current_api_key_masked: record.api_key_masked || t('clientConfig.notSet'),
    client_endpoint: record.client_endpoint || '',
    client_region: record.client_region || '',
    client_extra_config_json: record.client_extra_config ? JSON.stringify(record.client_extra_config, null, 2) : ''
  })
  clientConfigModalVisible.value = true
}

const handleClientConfigSave = async () => {
  try {
    let clientExtraConfig = null
    if (clientConfigForm.client_extra_config_json.trim()) {
      try {
        clientExtraConfig = JSON.parse(clientConfigForm.client_extra_config_json)
      } catch (error) {
        message.error(t('clientConfig.jsonFormatError'))
        return
      }
    }

    const updateData: any = {
      client_config_enabled: clientConfigForm.client_config_enabled,
      client_endpoint: clientConfigForm.client_endpoint || null,
      client_region: clientConfigForm.client_region || null,
      client_extra_config: clientExtraConfig
    }

    // 如果提供了新的API密钥，则更新
    if (clientConfigForm.api_key.trim()) {
      updateData.api_key = clientConfigForm.api_key.trim()
    }

    await clientConfigAdminApi.updateClientConfig(clientConfigForm.id, updateData)
    
    message.success(t('clientConfig.configUpdateSuccess'))
    clientConfigModalVisible.value = false
    refreshData()
  } catch (error) {
    console.error('更新客户端配置失败:', error)
  }
}

const handleClientConfigCancel = () => {
  clientConfigModalVisible.value = false
}

const showAccessLogsModal = async (record: APIConfig) => {
  accessLogsModalVisible.value = true
  await loadAccessLogs(record.id)
}

const loadAccessLogs = async (configId: number) => {
  try {
    accessLogsLoading.value = true
    const data = await clientConfigAdminApi.getClientAccessLogs(configId, {
      page: accessLogsPagination.current,
      size: accessLogsPagination.pageSize
    })
    
    accessLogs.value = data
    // 这里需要根据实际API响应调整分页信息
  } catch (error) {
    console.error('加载访问日志失败:', error)
  } finally {
    accessLogsLoading.value = false
  }
}

// 处理访问日志表格变化（排序和筛选）
// 注意：分页变化已经通过 accessLogsPagination 配置的 onChange/onShowSizeChange 处理
// @change 事件会传递三个参数：(pagination, filters, sorter)
const handleAccessLogsTableChange = (pag: any, filters: any, sorter: any) => {
  // 分页变化已经在 accessLogsPagination 的 onChange/onShowSizeChange 中处理
  // 这里只处理排序和筛选（如果需要的话）
}

const testClientConfig = async (record: APIConfig) => {
  try {
    record.testLoading = true
    // 这里可以添加测试客户端配置的逻辑
    message.success(t('clientConfig.clientConfigTestSuccess'))
  } catch (error) {
    console.error('测试失败:', error)
  } finally {
    record.testLoading = false
  }
}

// 工具函数
const getAPITypeColor = (type: string) => {
  const colors: Record<string, string> = {
    translation: 'blue',
    speech: 'green',
    ai_model: 'purple'
  }
  return colors[type] || 'default'
}

const getAPITypeLabel = (type: string) => {
  const option = API_TYPE_OPTIONS.find(opt => opt.value === type)
  return option ? option.label : type
}

const formatDateTime = (dateTime: string) => {
  const { locale } = useI18n()
  const timeLocale = locale.value === 'zh' ? 'zh-CN' : 'en-US'
  return new Date(dateTime).toLocaleString(timeLocale)
}

// 生命周期
onMounted(() => {
  loadApiConfigs()
})
</script>

<style scoped lang="less">
.client-config-management {
  padding: 24px;
}

.page-header {
  margin-bottom: 24px;
  
  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    
    h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      flex-shrink: 0;
    }
    
    .header-actions {
      flex-shrink: 0;
    }
  }
}

.stats-row {
  margin-bottom: 24px;
}

.search-card {
  margin-bottom: 24px;
}

.text-gray-400 {
  color: #9ca3af;
}

.form-help-text {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}
</style>
